import { Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ChartComponent, SbPieChartModel } from '@ngx-seatback/charts';
import { ErrorService } from '../../../../services/error.service';
import { API, CampaignWithPreviewDTO, LocalizedFramedReportingDTO, PlayoutAggregationDTO } from '../../../../services/api/api';
import { UtilService } from '../../../../services/util.service';
import { SbLocalDateTime } from '../../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { PermissionService } from '../../../../services/permission.service';
import { of, Subscription, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';

@Component({
  selector: 'sb-playouts-per-publisher',
  templateUrl: './playouts-per-publisher.component.html',
  styleUrls: ['./playouts-per-publisher.component.scss'],
  standalone: false,
})
export class PlayoutsPerPublisherComponent implements OnInit, OnDestroy {
  @ViewChild('chart') chart: ChartComponent;
  @ViewChild('chartLegend', { read: ElementRef, static: false }) chartLegendContainer: ElementRef;

  playoutChart: SbPieChartModel;
  campaigns: CampaignWithPreviewDTO[];
  loading: boolean = true;
  subs: Subscription[] = [];

  constructor(
    public errorHandler: ErrorService,
    private api: API,
    public util: UtilService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.playoutChart = {
      title: 'Playouts per Publisher',
      height: 150,
      value: 'value',
      category: 'category',
      fontSize: 30,
      data: [
        {
          category: 'Ströer',
          value: 50,
          color: '#1dc9b7',
        },
        {
          category: 'Philip Morris',
          value: 25,
          color: '#ff655b',
        },
        {
          category: 'Taxi-Ad GmbH',
          value: 25,
          color: '#ff7600',
        },
      ],
      loadingDone: true,
      tensionX: 1,
    };
  }

  ngOnInit(): void {
    if (this.permissionService.hasPermission(this.permissionService.Permission.campaign_list)) {
      this.subs.forEach(sub => sub.unsubscribe());
      this.subs = [];
      const sub = this.api
        .ds_cm_Campaign_retrieveAllFrontendEnhanced({
          timingStatus: null,
          company: null,
        })
        .pipe(
          switchMap(data => {
            this.campaigns = data;

            const pFrom = new SbLocalDateTime(DateTime.fromJSDate(this.util.getFromDate(7)));
            const pTill = new SbLocalDateTime(DateTime.local());
            const campaignIds: number[] = [];
            this.campaigns.forEach(x => {
              campaignIds.push(x.id);
            });
            if (campaignIds.length > 0) {
              return this.api.ds_cm_Reporting_campaignsReportGroupedBy('client', {
                id: campaignIds,
                interval: 86400,
                from: pFrom,
                till: pTill,
              });
            } else {
              return of(null);
            }
          }),
          takeUntilDestroyed(this.destroyRef),
          take(1)
        )
        .subscribe({
          next: reportingByClient => {
            if (reportingByClient) {
              const chartData = [];
              for (const client in reportingByClient) {
                if (client) {
                  const d1 = reportingByClient[client] as LocalizedFramedReportingDTO;
                  let playouts = 0;
                  d1.buckets.forEach(y => {
                    y.frames.forEach((x, i) => {
                      const frame = x.data as PlayoutAggregationDTO;
                      playouts += frame.productivePlayouts;
                    });
                  });
                  chartData.push({
                    category: client,
                    value: playouts,
                    color: '#1dc9b7',
                  });
                }
              }
              this.playoutChart.legend = true;
              this.playoutChart.legendContainer = this.chartLegendContainer;
              this.chart.chartData.legendContainerWidth = '600px';
              this.chart.chartData.legendAlign = 'right';
              this.playoutChart.data = chartData;
            }
            this.loading = false;
            if (this.chart) {
              this.chart.rerenderChart(this.playoutChart);
            }
          },
          error: error => {
            this.errorHandler.errorHandler(error);
            this.loading = false;
          },
        });
      this.subs.push(sub);
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
    this.subs = [];
  }
}
